'use client';

import {
  useCallback,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useModelPickerOptions } from '@/hooks/useModelPickerOptions';
import { useLogger } from '@/lib/client-logger';
import { registerDebugNamespace } from '@/lib/debug/registerDebugNamespace';
import { createDefaultWelcomeMessage, normalizeMessage } from '../chat-helpers';
import type { Message } from '../chat-types';
import { useChatStreaming } from '../hooks/streaming/useChatStreaming';
import { useChatBlurOverlay } from '../hooks/useChatBlurOverlay';
import { extractToolResultDigests } from '../utils/extractToolDigests';
import { ChatHeader } from './ChatHeader';
import { Composer } from './Composer';
import { useChatScroll } from './hooks/useChatScroll';
import { useKeyboardInset } from './hooks/useKeyboardInset';
import { useMessagesState } from './hooks/useMessagesState';
import { MessageList } from './MessageList';
import { StatusBar } from './StatusBar';
import { ToolSidebar } from './ToolSidebar';
import type { ChatInterfaceProperties, QueuedMessage } from './types';
import { collectActiveToolSessions } from './utils';

const NEAR_BOTTOM_DISTANCE = 160;
const ASSISTANT_DEPENDENCY_NONE = '__chat-assistant-none__';

export function ChatInterface({
  initialMessages,
  initialInput,
  initialSelectedModelId,
  initialModelData,
  onMessagesChange,
  onInputChange,
  onSelectedModelChange,
  onStartFresh,
  showWelcomeMessage,
  layout = 'floating',
  topOverlayHeight = 0,
  powerUserEnabled = false,
  variant = 'full',
  inputPlaceholder,
  onError,
  postPath,
  modelPickerEnabled = true,
  chatRef,
}: ChatInterfaceProperties) {
  const log = useLogger('chat');
  const shouldShowWelcome = showWelcomeMessage !== false;
  const showWelcomeReference = useRef(shouldShowWelcome);
  // Keep the ref aligned with the latest prop without scheduling another render cycle.
  showWelcomeReference.current = showWelcomeMessage !== false;

  const [messages, setMessages] = useMessagesState(() => {
    if (initialMessages && initialMessages.length > 0) {
      return initialMessages.map((message) => normalizeMessage(message));
    }
    return shouldShowWelcome ? [createDefaultWelcomeMessage()] : [];
  }, onMessagesChange);
  const [input, setInput] = useState(initialInput ?? '');
  const {
    options: modelOptions,
    isLoading: modelOptionsLoading,
    error: modelOptionsError,
    defaultId: defaultChatModelId,
  } = useModelPickerOptions({
    source: 'chat',
    initialData: initialModelData ?? null,
    enabled: modelPickerEnabled,
  });
  const [explicitModelId, setExplicitModelId] = useState<string | null>(
    initialSelectedModelId ?? initialModelData?.defaultId ?? null
  );
  const fallbackModelId = useMemo(() => {
    if (!modelPickerEnabled) {
      return null;
    }
    if (defaultChatModelId) {
      return defaultChatModelId;
    }
    return modelOptions[0]?.id ?? null;
  }, [defaultChatModelId, modelOptions, modelPickerEnabled]);
  const selectedModelId = explicitModelId ?? fallbackModelId;

  const keyboardInset = useKeyboardInset();

  const {
    scrollContainerRef,
    messagesEndRef,
    composerContainerRef,
    scrollContainerPaddingStyle,
    bottomBarInsetStyle,
    scrollToBottom,
    updateAtBottomFlag,
    getDistanceFromBottom,
    getScrollContainer,
    isAtBottom,
    isAtBottomRef,
  } = useChatScroll({ layout, topOverlayHeight, keyboardInset });
  const inputReference = useRef<HTMLTextAreaElement | null>(null);
  const isInitialRenderReference = useRef(true);
  const messagesReference = useRef<Message[]>([]);
  const pendingSubmissionIdsReference = useRef<Set<string>>(new Set());
  const submittedMessageIdsReference = useRef<Set<string>>(new Set());
  const [queuedInputs, setQueuedInputs] = useState<QueuedMessage[]>([]);
  const queuedInputsReference = useRef<QueuedMessage[]>([]);

  // Keep an imperative ref synchronized with the latest messages so background tasks
  // (streaming, queued submissions) always read the freshest array without scheduling
  // extra renders.
  messagesReference.current = messages;
  queuedInputsReference.current = queuedInputs;

  const isSidebarLayout = layout === 'sidebar';
  const showFullInterface = variant !== 'composer-only';

  const updateInput = useCallback(
    (value: string) => {
      setInput(value);
      onInputChange?.(value);
    },
    [onInputChange]
  );

  const clearInput = useCallback(() => {
    updateInput('');
  }, [updateInput]);

  const { blurRegion: computedBlurRegion, registerWrapper, registerMessageElement } = useChatBlurOverlay({ messages });
  const blurRegion = isSidebarLayout ? null : computedBlurRegion;

  const streamingErrorHandler = useCallback(
    (message: string) => {
      onError?.(message);
    },
    [onError]
  );
  const { isLoading, isSyncing, streamingState, latestUIEvent, handleSubmit, handleStop, submitUserMessage } =
    useChatStreaming({
      log,
      messagesRef: messagesReference,
      setMessages,
      selectedModelId,
      clearInput,
      scrollToBottom,
      onError: streamingErrorHandler,
      postPath,
    });

  const activeAssistantId = streamingState.assistantId;
  const assistantDependency = activeAssistantId ?? ASSISTANT_DEPENDENCY_NONE; // keep dependency array size stable

  useEffect(() => {
    registerDebugNamespace('chat', {
      messages,
      streamingState,
      latestUIEvent,
      queuedInputs,
      toolDigests: extractToolResultDigests(messages),
    });
  }, [latestUIEvent, messages, queuedInputs, streamingState]);

  // eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change -- welcome message bootstrapping depends on runtime state + prop-driven toggles.
  useEffect(() => {
    if (initialMessages && initialMessages.length > 0) {
      return;
    }
    setMessages((current) => {
      const hasNonWelcome = current.some((message) => message.id !== 'welcome');
      if (hasNonWelcome) {
        return current;
      }
      const hasWelcome = current.length > 0;
      if (showWelcomeMessage === false) {
        return hasWelcome ? [] : current;
      }
      if (!hasWelcome) {
        return [createDefaultWelcomeMessage()];
      }
      return current;
    });
  }, [initialMessages, setMessages, showWelcomeMessage]);

  useLayoutEffect(() => {
    if (messages.length === 0) {
      isAtBottomRef.current = true;
      return;
    }
    updateAtBottomFlag();
    if (isInitialRenderReference.current) {
      isInitialRenderReference.current = false;
      const initialContainer = getScrollContainer();
      if (initialContainer) {
        initialContainer.scrollTop = 0;
      }
      return;
    }

    const container = getScrollContainer();
    if (!container) {
      scrollToBottom();
      return;
    }

    if (isSidebarLayout && !isAtBottom()) {
      return;
    }

    const distanceFromBottom = getDistanceFromBottom();
    const isNearBottom = distanceFromBottom < NEAR_BOTTOM_DISTANCE;

    if (isNearBottom || streamingState.started) {
      scrollToBottom(streamingState.started ? 'smooth' : 'auto');
    }
  }, [
    getDistanceFromBottom,
    getScrollContainer,
    isAtBottom,
    isSidebarLayout,
    messages,
    scrollToBottom,
    streamingState.started,
    updateAtBottomFlag,
    isAtBottomRef,
  ]);

  const emitSelectedModelChange = useEffectEvent((modelId: string | null) => {
    if (typeof onSelectedModelChange === 'function') {
      onSelectedModelChange(modelId);
    }
  });

  /* eslint-disable react-you-might-not-need-an-effect/no-pass-live-state-to-parent */
  useEffect(() => {
    emitSelectedModelChange(selectedModelId ?? null);
  }, [selectedModelId]);
  /* eslint-enable react-you-might-not-need-an-effect/no-pass-live-state-to-parent */

  useEffect(() => {
    if (!modelOptionsLoading && modelOptions.length > 0) {
      log.info('Chat models loaded', {
        count: modelOptions.length,
        defaultModelId: defaultChatModelId ?? null,
      });
    }
  }, [log, modelOptionsLoading, modelOptions, defaultChatModelId]);

  useEffect(() => {
    if (modelOptionsError) {
      log.error('Failed to load chat models', { error: modelOptionsError });
    }
  }, [log, modelOptionsError]);

  useImperativeHandle(
    chatRef,
    () => ({
      resetSession: (options) => {
        pendingSubmissionIdsReference.current.clear();
        submittedMessageIdsReference.current.clear();
        setQueuedInputs([]);
        const nextMessages = options?.messages?.length
          ? options.messages.map((message) => normalizeMessage(message))
          : null;
        if (nextMessages && nextMessages.length > 0) {
          messagesReference.current = nextMessages;
          setMessages(nextMessages);
        } else {
          const fallbackMessages = showWelcomeReference.current ? [createDefaultWelcomeMessage()] : [];
          messagesReference.current = fallbackMessages;
          setMessages(fallbackMessages);
        }
        updateInput(options?.input ?? '');
        if (options?.selectedModelId !== undefined) {
          setExplicitModelId(options.selectedModelId);
        }
      },
      appendMessages: (newMessages) => {
        if (!Array.isArray(newMessages) || newMessages.length === 0) {
          return;
        }
        setMessages((previous) => {
          const normalized = newMessages.map((message) => normalizeMessage(message));
          const shouldRemoveWelcome = normalized.some((message) => message.id !== 'welcome');
          const base = shouldRemoveWelcome ? previous.filter((message) => message.id !== 'welcome') : previous;
          const nextMessages = [...base, ...normalized];
          for (const message of normalized) {
            if (message.role === 'user' && !submittedMessageIdsReference.current.has(message.id)) {
              pendingSubmissionIdsReference.current.add(message.id);
            }
          }
          messagesReference.current = nextMessages;
          return nextMessages;
        });
      },
      replaceMessages: (nextMessages) => {
        pendingSubmissionIdsReference.current.clear();
        submittedMessageIdsReference.current.clear();
        setQueuedInputs([]);
        const normalized = nextMessages.map((message) => normalizeMessage(message));
        messagesReference.current = normalized;
        setMessages(normalized);
      },
      getMessages: () => messagesReference.current,
      setSelectedModel: (modelId) => {
        setExplicitModelId(modelId);
      },
      setInput: (value) => {
        updateInput(value);
      },
      focusInput: () => {
        const textarea = inputReference.current;
        if (!textarea) {
          return;
        }
        try {
          textarea.focus({ preventScroll: true });
        } catch {
          textarea.focus();
        }
        const length = textarea.value.length;
        textarea.setSelectionRange(length, length);
      },
      submitUserMessage,
    }),
    [setMessages, submitUserMessage, updateInput]
  );

  useLayoutEffect(() => {
    if (messages.length === 0) {
      pendingSubmissionIdsReference.current.clear();
      submittedMessageIdsReference.current.clear();
      return;
    }

    const visibleMessageIds = new Set(messages.map((message) => message.id));
    for (const id of submittedMessageIdsReference.current) {
      if (!visibleMessageIds.has(id)) {
        submittedMessageIdsReference.current.delete(id);
      }
    }
    for (const id of pendingSubmissionIdsReference.current) {
      if (!visibleMessageIds.has(id)) {
        pendingSubmissionIdsReference.current.delete(id);
      }
    }

    let index = 0;
    for (const message of messages) {
      if (!message || message.role !== 'user') {
        index += 1;
        continue;
      }

      if (submittedMessageIdsReference.current.has(message.id)) {
        index += 1;
        continue;
      }

      const hasAssistantAfter = messages.slice(index + 1).some((entry) => entry.role === 'assistant');
      if (hasAssistantAfter) {
        submittedMessageIdsReference.current.add(message.id);
        index += 1;
        continue;
      }

      if (pendingSubmissionIdsReference.current.has(message.id)) {
        index += 1;
        continue;
      }

      pendingSubmissionIdsReference.current.add(message.id);
      index += 1;
    }
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) {
      pendingSubmissionIdsReference.current.clear();
      return;
    }

    const visibleMessageIds = new Set(messages.map((message) => message.id));
    for (const id of pendingSubmissionIdsReference.current) {
      if (!visibleMessageIds.has(id)) {
        pendingSubmissionIdsReference.current.delete(id);
      }
    }

    if (pendingSubmissionIdsReference.current.size === 0) {
      return;
    }
    const ids = [...pendingSubmissionIdsReference.current];
    pendingSubmissionIdsReference.current.clear();
    const abortController = new AbortController();
    const { signal } = abortController;
    const processPendingSubmissions = async () => {
      for (const id of ids) {
        if (signal.aborted) {
          return;
        }
        try {
          const submitted = await submitUserMessage(id);
          if (signal.aborted) {
            return;
          }
          if (!submitted) {
            const history = messagesReference.current;
            const messageIndex = history.findIndex((entry) => entry.id === id);
            if (messageIndex === -1) {
              continue;
            }
            const hasAssistantAfter = history.slice(messageIndex + 1).some((entry) => entry.role === 'assistant');
            if (hasAssistantAfter) {
              submittedMessageIdsReference.current.add(id);
              continue;
            }
            const isStillVisible = history.some((entry) => entry.id === id);
            if (isStillVisible) {
              pendingSubmissionIdsReference.current.add(id);
            }
          }
        } catch {
          // errors handled by hook
        }
      }
    };

    processPendingSubmissions().catch(() => {
      // errors handled by hook
    });
    return () => {
      abortController.abort();
    };
  }, [messages, submitUserMessage]);

  const submitMessage = useCallback(
    (text: string, context: 'composer' | 'suggestion' | 'queued') => {
      handleSubmit(text).catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'Unknown error';
        log.error('Failed to submit chat message', { context, error: message });
      });
    },
    [handleSubmit, log]
  );

  useEffect(() => {
    if (isLoading || assistantDependency !== ASSISTANT_DEPENDENCY_NONE) {
      return;
    }
    const [nextEntry, ...remaining] = queuedInputsReference.current;
    if (!nextEntry) {
      return;
    }
    setQueuedInputs(remaining);
    if (!nextEntry.text) {
      return;
    }
    log.info('Submitting queued chat message', {
      textPreview: nextEntry.text.slice(0, 80),
      textLength: nextEntry.text.length,
      remainingQueued: remaining.length,
    });
    submitMessage(nextEntry.text, 'queued');
  }, [assistantDependency, isLoading, log, submitMessage]);

  const handleModelChange = useCallback(
    (modelId: string) => {
      const selectedOption = modelOptions.find((option) => option.id === modelId);
      setExplicitModelId((previous) => {
        if (previous === modelId) {
          log.info('Chat model selection unchanged', { modelId });
          return previous;
        }

        if (previous === null) {
          log.info('Chat model initialized', {
            modelId,
            label: selectedOption?.displayName ?? null,
          });
        } else {
          log.info('Chat model selection changed', {
            previousModelId: previous,
            modelId,
            previousLabel: modelOptions.find((option) => option.id === previous)?.displayName ?? null,
            nextLabel: selectedOption?.displayName ?? null,
          });
        }

        return modelId;
      });
    },
    [log, modelOptions]
  );

  const handleRemoveQueuedInput = useCallback((entryId: string) => {
    setQueuedInputs((previous) => previous.filter((entry) => entry.id !== entryId));
  }, []);

  const toolSessions = useMemo(() => collectActiveToolSessions(messages), [messages]);

  const statusInfo = useMemo(
    () => ({
      isLoading,
      isSyncing,
      streamingState,
      queuedMessages: queuedInputs,
      recentEvent: latestUIEvent,
    }),
    [isLoading, isSyncing, streamingState, queuedInputs, latestUIEvent]
  );

  const composerDisabled = false;

  const submitText = useCallback(
    (trimmed: string, source: 'composer' | 'suggestion') => {
      if (!trimmed) return;

      if (isLoading || streamingState.assistantId !== null) {
        log.info('Queueing chat message', {
          textPreview: trimmed.slice(0, 80),
          textLength: trimmed.length,
          source,
        });
        setQueuedInputs((previous) => [
          ...previous,
          { id: `queued-${source}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, text: trimmed },
        ]);
        updateInput('');
        return;
      }

      submitMessage(trimmed, source);
    },
    [isLoading, log, streamingState.assistantId, submitMessage, updateInput]
  );

  const rootClassName = (() => {
    if (!showFullInterface) {
      return 'relative flex h-full w-full min-h-0 flex-col';
    }
    return isSidebarLayout
      ? 'relative flex h-full w-full min-h-0 flex-col'
      : 'relative flex h-full w-full min-h-0 flex-col lg:flex-row';
  })();

  return (
    <div className={rootClassName}>
      {showFullInterface ? (
        <>
          <div className="flex h-full min-h-0 flex-1 flex-col">
            <ChatHeader layout={layout} onStartFresh={onStartFresh} isSyncing={isSyncing} />
            <MessageList
              messages={messages}
              layout={layout}
              powerUserEnabled={powerUserEnabled}
              scrollContainerRef={scrollContainerRef}
              messagesEndRef={messagesEndRef}
              paddingStyle={scrollContainerPaddingStyle}
              blurRegion={blurRegion}
              showSyncIndicator={false}
              registerWrapper={registerWrapper}
              registerMessageElement={registerMessageElement}
              onSuggestionSelect={(text) => {
                const trimmed = text.trim();
                if (!trimmed) {
                  return;
                }
                submitText(trimmed, 'suggestion');
              }}
            />
            <StatusBar
              layout={layout}
              showSyncIndicator={streamingState.assistantId === null && isSyncing}
              {...statusInfo}
            />
          </div>
          <ToolSidebar layout={layout} sessions={toolSessions} />
        </>
      ) : null}
      <Composer
        inputRef={inputReference}
        layout={layout}
        containerRef={composerContainerRef}
        bottomBarInsetStyle={bottomBarInsetStyle}
        keyboardInset={keyboardInset}
        value={input}
        onChange={updateInput}
        onSubmit={(text) => {
          const trimmed = text.trim();
          if (!trimmed) {
            return;
          }
          submitText(trimmed, 'composer');
        }}
        disabled={composerDisabled}
        onStop={handleStop}
        isLoading={isLoading}
        modelOptions={modelPickerEnabled ? modelOptions : []}
        selectedModelId={modelPickerEnabled ? selectedModelId : null}
        onModelChange={handleModelChange}
        isModelOptionsLoading={modelPickerEnabled ? modelOptionsLoading : false}
        modelOptionsError={modelPickerEnabled ? modelOptionsError : null}
        onStartFresh={onStartFresh}
        queuedMessages={queuedInputs}
        onRemoveQueuedMessage={queuedInputs.length > 0 ? handleRemoveQueuedInput : undefined}
        inputPlaceholder={inputPlaceholder}
        modelPickerEnabled={modelPickerEnabled}
      />
    </div>
  );
}
